import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { wellbeing, badges, REPORTER_TARGET } from "@features/programs/utils/gamification";
import { WellbeingSpark } from "./WellbeingSpark";
import { BadgeToken } from "./BadgeToken";
import "./gamification.css";

/**
 * What answering the feedback question gives back, shown right under the
 * form: the patient's own curve with today's value as the last point, and —
 * until it is earned — how close the "Rückmelder" badge is, moving as soon
 * as a message is typed.
 */
export function FeedbackReward({ program, programs = null, pendingValue, pendingFeedback = "" }) {
  const { t } = useTranslation();
  const trend = useMemo(() => wellbeing(program), [program]);
  const reporter = useMemo(
    () => badges(programs?.length ? programs : [program]).find((badge) => badge.id === "reporter"),
    [programs, program]
  );

  const value = Number(pendingValue);
  const points = Number.isFinite(value)
    ? [...trend.points, { date: new Date(), value, pending: true }]
    : trend.points;
  const delta = points.length >= 2 ? points[points.length - 1].value - points[0].value : null;

  const typed = String(pendingFeedback || "").trim() !== "" ? 1 : 0;
  const reporterCurrent = reporter?.progress
    ? Math.min(REPORTER_TARGET, reporter.progress.current + typed)
    : REPORTER_TARGET;

  return (
    <div className="gm gm-card gm-reward">
      <div className="gm-reward-part">
        <div className="gm-reward-top">
          <b>{t("gamification.reward.trend")}</b>
          {delta !== null && (
            <span>
              {delta > 0 ? "+" : ""}
              {delta} {t("gamification.wellbeing.since_start")}
            </span>
          )}
        </div>
        <WellbeingSpark points={points} />
        <p className="gm-reward-note">
          {t(points.length >= 2 ? "gamification.wellbeing.hint_today" : "gamification.wellbeing.hint")}
        </p>
      </div>

      {reporter && !reporter.earned && (
        <div className="gm-reward-part">
          <div className="gm-reward-row">
            <BadgeToken id="reporter" locked size="sm" />
            <div className="gm-reward-row-body">
              <div className="gm-reward-top">
                <b>{t("gamification.reward.badge", { name: t("gamification.badges.reporter.name") })}</b>
                <span>
                  {reporterCurrent} / {REPORTER_TARGET}
                </span>
              </div>
              <div className="gm-mini-track">
                <span style={{ width: `${(reporterCurrent / REPORTER_TARGET) * 100}%` }} />
              </div>
              <p className="gm-reward-note">
                {t("gamification.reward.reporter_hint", { count: Math.max(0, REPORTER_TARGET - reporterCurrent) })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeedbackReward;
